/**
 * Individual note item with checkbox for todo functionality
 */

import { useState, useCallback } from 'react'
import { Check, Pencil, Trash2 } from 'lucide-react'
import { cn } from '@/utils/misc'
import type { CalendarNote } from '@/types/calendar'

interface NoteItemProps {
	note: CalendarNote
	onToggle: (id: string) => void
	onEdit: (note: CalendarNote) => void
	onDelete: (id: string) => void
	isToggling?: boolean
}

export function NoteItem({
	note,
	onToggle,
	onEdit,
	onDelete,
	isToggling = false,
}: NoteItemProps) {
	const [showActions, setShowActions] = useState(false)

	const handleToggle = useCallback(() => {
		if (!isToggling) {
			onToggle(note.id)
		}
	}, [note.id, onToggle, isToggling])

	const handleEdit = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation()
			onEdit(note)
		},
		[note, onEdit],
	)

	const handleDelete = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation()
			onDelete(note.id)
		},
		[note.id, onDelete],
	)

	return (
		<div
			className={cn(
				'group flex items-center gap-1.5 px-1.5 py-1 rounded-md transition-colors cursor-pointer min-w-0',
				'hover:bg-[#f5ebe0]',
				note.isDone && 'opacity-60',
			)}
			onMouseEnter={() => setShowActions(true)}
			onMouseLeave={() => setShowActions(false)}
			onClick={handleToggle}
		>
			{/* Color indicator */}
			{note.color && (
				<span
					className="w-2.5 h-4 rounded-sm flex-shrink-0"
					style={{ backgroundColor: note.color }}
				/>
			)}

			{/* Checkbox */}
			<button
				type="button"
				onClick={handleToggle}
				disabled={isToggling}
				className={cn(
					'flex-shrink-0 w-3.5 h-3.5 rounded border transition-colors',
					'flex items-center justify-center',
					note.isDone
						? 'bg-[#d7ad6c] border-[#d7ad6c] text-white'
						: 'border-[#a09689] hover:border-[#d7ad6c]',
					isToggling && 'opacity-50 cursor-not-allowed',
				)}
			>
				{note.isDone && <Check className="w-2.5 h-2.5" />}
			</button>

			{/* Time badge */}
			<span
				className="flex-shrink-0 text-[#8c8275] bg-[#f0ede8] px-1 py-0.5 rounded"
				style={{ fontSize: 'var(--font-size-note-time)' }}
			>
				{note.timeFrom}
			</span>

			{/* Note text */}
			<span
				className={cn(
					'flex-1 min-w-0 text-[#3d3428] truncate',
					note.isDone && 'line-through text-[#8c8275]',
				)}
				style={{ fontSize: 'var(--font-size-note-text)' }}
			>
				{note.text}
			</span>

			{/* Action buttons */}
			{showActions && (
				<div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
					<button
						type="button"
						onClick={handleEdit}
						className="p-1 rounded hover:bg-[#e8e4df] text-[#8c8275] hover:text-[#5c5142] transition-colors"
						title="Upravit"
					>
						<Pencil className="w-3 h-3" />
					</button>
					<button
						type="button"
						onClick={handleDelete}
						className="p-1 rounded hover:bg-red-100 text-[#8c8275] hover:text-red-600 transition-colors"
						title="Smazat"
					>
						<Trash2 className="w-3 h-3" />
					</button>
				</div>
			)}
		</div>
	)
}
